import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Trash2, Edit2, Plus, X, Save, ArrowRight, GripHorizontal, List, Grid } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from '../../api/apiClient';
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Reorder } from "framer-motion";

// API
const adminApi = {
    getRules: apiClient.admin.getCategoriesRules,
    getUnmapped: apiClient.admin.getUnmappedStyles,
    createRule: apiClient.admin.createCategoryRule,
    updateRule: ({ id, data }) => apiClient.admin.updateCategoryRule(id, data),
    deleteRule: apiClient.admin.deleteCategoryRule,
    seedRules: apiClient.admin.seedCategoryRules,
    reorderRules: apiClient.admin.reorderCategoryRules
};


export default function AdminCategories() {
    const queryClient = useQueryClient();
    const [isCreating, setIsCreating] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [isReordering, setIsReordering] = useState(false);
    const [orderedRules, setOrderedRules] = useState([]);
    const [localRules, setLocalRules] = useState([]);

    // Data
    const { data: rules = [] } = useQuery({
        queryKey: ['categoryRules'],
        queryFn: adminApi.getRules,
        staleTime: 0,
        onSuccess: (data) => {
            setOrderedRules(data); // Init local order
            setLocalRules(data);
        }
    });

    // Update local state when rules change from server (if not reordering)
    React.useEffect(() => {
        if (!isReordering && rules.length > 0) {
            setOrderedRules(rules);
            setLocalRules(rules);
        }
    }, [rules, isReordering]);
    const { data: unmapped = [] } = useQuery({ queryKey: ['unmappedStyles'], queryFn: adminApi.getUnmapped });

    // Mutations
    const createMutation = useMutation({
        mutationFn: adminApi.createRule,
        onSuccess: () => {
            queryClient.invalidateQueries(['categoryRules']);
            queryClient.invalidateQueries(['unmappedStyles']);
        }
    });

    const updateMutation = useMutation({
        mutationFn: adminApi.updateRule,
        onSuccess: () => {
            queryClient.invalidateQueries(['categoryRules']);
            queryClient.invalidateQueries(['unmappedStyles']);
        }
    });

    const deleteMutation = useMutation({
        mutationFn: adminApi.deleteRule,
        onSuccess: () => {
            queryClient.invalidateQueries(['categoryRules']);
            queryClient.invalidateQueries(['unmappedStyles']);
        }
    });

    const seedMutation = useMutation({
        mutationFn: adminApi.seedRules,
        onSuccess: () => {
            queryClient.invalidateQueries(['categoryRules']);
            queryClient.invalidateQueries(['unmappedStyles']);
        }
    });

    const reorderMutation = useMutation({
        mutationFn: adminApi.reorderRules,
        onSuccess: () => {
            queryClient.invalidateQueries(['categoryRules']);
        }
    });

    // Handlers
    const handleCreateCategory = (name = newCategoryName, keywords = []) => {
        if (!name.trim()) return;
        createMutation.mutate({
            name,
            keywords,
            priority: rules.length + 1 // Default priority at end
        });
        setNewCategoryName('');
        setIsCreating(false);
    };

    const handleAddKeyword = (rule, keyword) => {
        if (!keyword.trim()) return;
        if (rule.keywords.includes(keyword)) return;

        updateMutation.mutate({
            id: rule.id,
            data: { ...rule, keywords: [...rule.keywords, keyword] }
        });
    };

    const handleRemoveKeyword = (rule, keywordToRemove) => {
        updateMutation.mutate({
            id: rule.id,
            data: { ...rule, keywords: rule.keywords.filter(k => k !== keywordToRemove) }
        });
    };

    const handleDeleteCategory = (id) => {
        if (confirm("Supprimer cette catégorie ?")) {
            deleteMutation.mutate(id);
        }
    };

    const handleSeedDefaults = () => {
        if (confirm("Voulez-vous restaurer les catégories par défaut ?")) {
            seedMutation.mutate();
        }
    };

    const handleSaveOrder = () => {
        const ids = orderedRules.map(r => r.id);
        reorderMutation.mutate(ids);
        setIsReordering(false);
    };

    return (
        <div className="flex flex-col md:flex-row h-screen md:h-[calc(100vh-65px)] overflow-hidden bg-gray-50">
            {/* Main Content - Categories Grid */}
            <div className="flex-1 p-8 overflow-auto">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-3xl font-bold">Configuration des Catégories</h1>
                        <p className="text-gray-500">Gérez comment les vêtements sont classés et détectés.</p>
                    </div>

                    <div className="flex gap-2">
                        <Input
                            placeholder="Nouvelle catégorie..."
                            className="w-64 bg-white"
                            value={newCategoryName}
                            onChange={e => setNewCategoryName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleCreateCategory()}
                        />
                        <Button variant="outline" onClick={handleSeedDefaults} title="Restaurer les catégories par défaut manquantes">
                            Restaurer
                        </Button>
                        <Button
                            variant={isReordering ? "secondary" : "outline"}
                            onClick={() => setIsReordering(!isReordering)}
                            title="Mode réorganisation"
                        >
                            {isReordering ? <Grid className="h-4 w-4 mr-2" /> : <List className="h-4 w-4 mr-2" />}
                            {isReordering ? "Terminer" : "Réorganiser"}
                        </Button>
                        {isReordering && (
                            <Button onClick={handleSaveOrder} className="bg-green-600 hover:bg-green-700">
                                <Save className="h-4 w-4 mr-2" /> Sauvegarder
                            </Button>
                        )}
                        {!isReordering && (
                            <Button onClick={() => handleCreateCategory()}>
                                <Plus className="mr-2 h-4 w-4" /> Créer
                            </Button>
                        )}
                    </div>
                </div>

                <div className="pb-20">
                    {isReordering ? (
                        <Reorder.Group axis="y" values={orderedRules} onReorder={setOrderedRules} className="space-y-2 max-w-3xl mx-auto">
                            {orderedRules.map(rule => (
                                <Reorder.Item key={rule.id} value={rule}>
                                    <div className="flex items-center gap-4 p-4 bg-white border rounded-lg shadow-sm cursor-move hover:shadow-md">
                                        <GripHorizontal className="text-gray-400" />
                                        <div className="flex-1">
                                            <h3 className="font-bold">{rule.name}</h3>
                                            <p className="text-xs text-gray-500">Priorité: {rule.priority}</p>
                                        </div>
                                        <Badge variant="secondary">{rule.keywords.length} mots-clés</Badge>
                                    </div>
                                </Reorder.Item>
                            ))}
                        </Reorder.Group>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 content-start">
                            {localRules.map(rule => (
                                <CategoryCard
                                    key={rule.id}
                                    rule={rule}
                                    onAddKeyword={handleAddKeyword}
                                    onRemoveKeyword={handleRemoveKeyword}
                                    onDelete={() => handleDeleteCategory(rule.id)}
                                    onUpdate={(data) => updateMutation.mutate({ id: rule.id, data })}
                                />
                            ))}
                        </div>
                    )}

                    {rules.length === 0 && !isReordering && (
                        <div className="col-span-full flex flex-col items-center justify-center p-12 text-gray-400 border-2 border-dashed rounded-lg">
                            <GripHorizontal className="h-12 w-12 mb-4 opacity-20" />
                            <p>Aucune catégorie configurée.</p>
                            <p className="text-sm mb-4">Utilisez le panneau latéral pour promouvoir des styles ou créez une catégorie manuellement.</p>
                            <Button variant="outline" onClick={handleSeedDefaults}>
                                Restaurer les défauts
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Sidebar - Unmapped Styles */}
            <div className="w-full md:w-80 bg-white border-l shadow-xl flex flex-col z-10 h-64 md:h-auto">
                <div className="p-4 border-b bg-gray-50/50">
                    <h2 className="font-semibold flex items-center gap-2">
                        <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs font-bold">{unmapped.length}</span>
                        Styles Non Classés
                        {unmapped.length >= 5000 && <span className="text-xs text-red-500 font-normal ml-2">(Max 5k)</span>}
                    </h2>
                    <p className="text-xs text-gray-500 mt-1">
                        Styles détectés non liés à une catégorie.
                    </p>
                </div>

                <ScrollArea className="flex-1 p-4">
                    <div className="space-y-2">
                        {unmapped.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-4">Tout est bien rangé ! 🎉</p>
                        ) : unmapped.map(style => (
                            <div key={style} className="group flex items-center justify-between p-2 rounded-md border border-gray-100 hover:border-amber-200 hover:bg-amber-50 transition-all bg-white text-sm">
                                <span className="truncate flex-1 font-medium text-gray-700">{style}</span>

                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {/* Promote to new Category */}
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-6 w-6 text-amber-600 hover:text-amber-700 hover:bg-amber-100"
                                        title="Créer une catégorie"
                                        onClick={() => handleCreateCategory(style.charAt(0).toUpperCase() + style.slice(1), [style])}
                                    >
                                        <Plus className="h-3 w-3" />
                                    </Button>

                                    {/* Add to existing Category */}
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                className="h-6 w-6 text-amber-600 hover:text-amber-700 hover:bg-amber-100"
                                                title="Ajouter à une catégorie"
                                            >
                                                <ArrowRight className="h-3 w-3" />
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="p-2 w-48 max-h-64 overflow-auto" align="end">
                                            <div className="space-y-1">
                                                <p className="text-xs font-semibold text-gray-500 mb-2 px-1">Déplacer vers...</p>
                                                {rules.length === 0 && <p className="text-xs text-gray-400 px-1">Aucune catégorie</p>}
                                                {rules.map(rule => (
                                                    <button
                                                        key={rule.id}
                                                        className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-gray-100 flex items-center justify-between group/item"
                                                        onClick={() => handleAddKeyword(rule, style)}
                                                    >
                                                        <span className="font-medium text-gray-700">{rule.name}</span>
                                                        <Plus className="h-3 w-3 opacity-0 group-hover/item:opacity-100 text-gray-400" />
                                                    </button>
                                                ))}
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            </div>
                        ))}
                    </div>
                </ScrollArea>
            </div>
        </div>
    );
}

function CategoryCard({ rule, onAddKeyword, onRemoveKeyword, onDelete, onUpdate }) {
    const [input, setInput] = useState('');
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [isEditingPriority, setIsEditingPriority] = useState(false);
    const [titleInput, setTitleInput] = useState(rule.name);
    const [priorityInput, setPriorityInput] = useState(rule.priority);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (input.trim()) {
                onAddKeyword(rule, input.trim());
                setInput('');
            }
        }
    };

    const handleTitleSave = () => {
        if (titleInput.trim() !== rule.name) {
            onUpdate({ ...rule, name: titleInput.trim() });
        }
        setIsEditingTitle(false);
    };

    const handlePrioritySave = () => {
        const newPrio = parseInt(priorityInput, 10);
        if (!isNaN(newPrio) && newPrio !== rule.priority) {
            onUpdate({ ...rule, priority: newPrio });
        } else {
            setPriorityInput(rule.priority);
        }
        setIsEditingPriority(false);
    };

    return (
        <Card className="flex flex-col h-full bg-white shadow-sm border-gray-100 hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 flex flex-row items-start justify-between space-y-0">
                <div className="flex-1">
                    {isEditingTitle ? (
                        <Input
                            value={titleInput}
                            onChange={e => setTitleInput(e.target.value)}
                            onBlur={handleTitleSave}
                            onKeyDown={e => e.key === 'Enter' && handleTitleSave()}
                            className="h-7 text-lg font-bold px-1"
                        />
                    ) : (
                        <CardTitle
                            className="text-lg font-bold cursor-pointer hover:underline decoration-dashed underline-offset-4 decoration-gray-300"
                            onClick={() => setIsEditingTitle(true)}
                        >
                            {rule.name}
                        </CardTitle>
                    )}
                    <CardDescription className="text-xs mt-1">
                        {isEditingPriority ? (
                            <div className="flex items-center gap-1">
                                <span>Priorité:</span>
                                <Input
                                    type="number"
                                    value={priorityInput}
                                    onChange={e => setPriorityInput(e.target.value)}
                                    onBlur={handlePrioritySave}
                                    onKeyDown={e => e.key === 'Enter' && handlePrioritySave()}
                                    className="h-6 w-16 text-xs px-1"
                                    autoFocus
                                />
                            </div>
                        ) : (
                            <span className="cursor-pointer hover:underline decoration-dashed decoration-gray-300" onClick={() => setIsEditingPriority(true)}>
                                Priorité: {rule.priority}
                            </span>
                        )}
                    </CardDescription>
                </div>
                <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400 hover:text-red-500" onClick={onDelete}>
                    <Trash2 className="h-3 w-3" />
                </Button>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col gap-3">
                <div className="flex flex-wrap gap-1.5 min-h-[50px] content-start">
                    {rule.keywords.map(keyword => (
                        <Badge key={keyword} variant="secondary" className="pr-1 hover:bg-amber-100 transition-colors group cursor-default">
                            {keyword}
                            <button
                                onClick={() => onRemoveKeyword(rule, keyword)}
                                className="ml-1 rounded-full p-0.5 hover:bg-red-200 hover:text-red-700 opacity-50 group-hover:opacity-100 transition-all"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </Badge>
                    ))}
                    {rule.keywords.length === 0 && <span className="text-xs text-gray-400 italic p-1">Vide</span>}
                </div>

                <div className="mt-auto pt-2 relative">
                    <Input
                        className="h-8 text-xs pr-8"
                        placeholder="+ Ajouter mot-clé..."
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    {input && (
                        <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 absolute right-1 top-3 text-amber-600"
                            onClick={() => { onAddKeyword(rule, input); setInput(''); }}
                        >
                            <Plus className="h-3 w-3" />
                        </Button>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
